import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import Loading from "../../../../common-components/Loading";
import { getMonthlyTMByLcatAPI } from "../../../../api/reports-api";
import {
  aggregateMonthlyTmGrandTotal,
  hasActiveNumFilters,
  hasActiveTextFilters,
  hasAnyTableFilters,
} from "../tableTotalUtils";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "../waterfall-eac/waterfallTableUtils";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import { NumericFilterContent, TextFilterContent, OpDropdownContent, fpBase } from "../waterfall-eac/tableColumnFilterShared";
import { getGlobalPeriodHiddenKeys } from "../pl-by-program/plTimeFilterUtils";
import {
  TABLE_FONT,
  TH_BG,
  TH_TEXT,
  TH_SHADOW,
  CELL_FG,
  CELL_FG_STRONG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  Z_HEAD,
  Z_STICKY,
  TH_HEIGHT,
  ROW_HEIGHT,
  ACCENT_COLOR,
  CELL_FG_MUTED,
  TH_BG_BAND,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../../reportTableConfig";

const FONT = TABLE_FONT;
const TOTAL_FG = CELL_FG_STRONG;
const SC_ACCENT = ACCENT_COLOR;
const CH = TH_HEIGHT;
const RH = ROW_HEIGHT;

/* ── Month metadata ── */
const MONTH_KEYS   = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const MONTH_LABELS = { jan:"Jan",feb:"Feb",mar:"Mar",apr:"Apr",may:"May",jun:"Jun",jul:"Jul",aug:"Aug",sep:"Sep",oct:"Oct",nov:"Nov",dec:"Dec" };
const MONTH_NUM    = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };

/* ── Column definitions ── */
const FIXED_COLS = [
  { key: "lcat",     label: "LCAT",      w: 120, align: "left",  isText: true  },
  { key: "lcatDesc", label: "LCAT Desc", w: 260, align: "left",  isText: true  },
  { key: "billRate", label: "Billrate",  w: 160, align: "right", isText: false },
];
let _left = 0;
FIXED_COLS.forEach((c) => { c.left = _left; _left += c.w; });

const COL_HOURS   = 80;
const COL_DOLLARS = 110;

/* ── Operator filter constants ── */
const OP_LABELS  = { gt:">", gte:"≥", lt:"<", lte:"≤", eq:"=", neq:"≠" };
const OP_ENTRIES = [
  { key:"gt",  label:"> Greater than" },
  { key:"gte", label:"≥ Greater than or equal" },
  { key:"lt",  label:"< Less than" },
  { key:"lte", label:"≤ Less than or equal" },
  { key:"eq",  label:"= Equals" },
  { key:"neq", label:"≠ Not equal" },
];

function applyNumFilter(rawVal, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const v  = rawVal == null ? 0 : Number(rawVal);
  const fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":  return v > fv;
    case "gte": return v >= fv;
    case "lt":  return v < fv;
    case "lte": return v <= fv;
    case "eq":  return v === fv;
    case "neq": return v !== fv;
    default:    return true;
  }
}

/* ── Palette (reportTableConfig) ── */
const GH = 32;
const CARD_META = { fontSize: 12, color: CELL_FG_MUTED, marginTop: 4, fontFamily: FONT };

/* ── Table header styles ── */
const thCore = (top, h, z, padding) => ({
  position: "sticky", top, zIndex: z, height: h, lineHeight: 1.25,
  boxSizing: "border-box", background: TH_BG, color: TH_TEXT,
  verticalAlign: "middle", padding, whiteSpace: "nowrap", border: "none",
  boxShadow: TH_SHADOW,
});
const thFixedGroup = (w) => ({
  ...thCore(0, GH, Z_HEAD + 1, "10px 16px"),
  left: 0, width: w, minWidth: w, maxWidth: w,
});
const thFixedCol = (col, isLast) => ({
  ...thCore(GH, CH, Z_HEAD + 1, "10px 14px"),
  left: col.left, width: col.w, minWidth: col.w, maxWidth: col.w,
  fontSize: 11, fontWeight: 500, textAlign: col.align,
  boxShadow: isLast ? `4px 0 8px -2px rgba(0,0,0,0.18), ${TH_SHADOW}` : TH_SHADOW,
});
const thScrollGroup = (isActual) => ({
  ...thCore(0, GH, Z_HEAD, "10px 10px"),
  fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
  textAlign: "center",
  ...(isActual
    ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT }
    : { color: TH_TEXT }),
});
const thScrollCol = (isActual) => ({
  ...thCore(GH, CH, Z_HEAD, "10px 8px"),
  fontSize: 11, textAlign: "right",
  fontWeight: isActual ? 700 : 500,
  ...(isActual
    ? { background: TH_ACTUAL_BG, color: TH_ACTUAL_TEXT }
    : { color: TH_TEXT }),
});
const tdBase = (bg, align = "right", fw = 400) => ({
  boxSizing: "border-box", background: bg, textAlign: align, fontWeight: fw,
  padding: "7px 10px", fontSize: 12, color: CELL_FG, whiteSpace: "nowrap",
  verticalAlign: "middle", fontVariantNumeric: "tabular-nums", borderBottom: "1px solid #E2E8F0",
});
const tdFixed = (bg, col, fw = 400, isLast = false) => ({
  ...tdBase(bg, col.align, fw),
  position: "sticky", left: col.left, zIndex: Z_STICKY,
  width: col.w, minWidth: col.w, maxWidth: col.w,
  boxShadow: isLast ? "4px 0 8px -2px rgba(0,0,0,0.06)" : undefined,
});
const tdTotal = (col, isLast = false) => ({
  ...tdFixed(TOTAL_BG, col, 700, isLast), color: TOTAL_FG, borderBottom: "1px solid #CBD5E1",
});
const tdTotalScroll = () => ({
  ...tdBase(TOTAL_BG, "right", 600), color: TOTAL_FG, borderBottom: "1px solid #CBD5E1",
});

/* ── Card / head styles ── */
const CARD  = { background:"#fff", borderRadius:12, border:"1px solid #EBEBEB", boxShadow:"0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)", overflow:"visible", display:"flex", flexDirection:"column", marginBottom:20 };
const HEAD  = { padding:"16px 20px 12px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, position:"relative", zIndex:20, overflow:"visible", flexShrink:0, background:"#fff", borderTopLeftRadius:12, borderTopRightRadius:12 };
const CARD_BODY = { overflow:"hidden", borderBottomLeftRadius:12, borderBottomRightRadius:12 };
const TITLE = { fontSize:14, fontWeight:600, color:"#111827", letterSpacing:"-0.01em", fontFamily:FONT, margin:0 };

/* ── Filter icon ── */
function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/* ── Filter / column picker styles ── */
const filterIconBtn = (active, isActual = false) => ({
  flexShrink: 0, width: 16, height: 16, border: "none",
  background: active
    ? (isActual ? "rgba(0,0,0,0.12)" : "rgba(165,180,252,0.18)")
    : "transparent",
  cursor: "pointer", padding: 0, borderRadius: 3,
  color: isActual ? TH_ACTUAL_TEXT : (active ? "#A5B4FC" : TH_TEXT),
  opacity: active || isActual ? 1 : 0.5,
  display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle",
});
const filterPanelSt = {
  position:"fixed", zIndex:99, background:"#fff",
  border:"1px solid #E2E8F0", borderRadius:8,
  boxShadow:"0 8px 24px rgba(0,0,0,0.12)", padding:"10px 12px", minWidth:190, fontFamily:FONT,
};
const filterWrap      = { display:"flex", alignItems:"center", gap:3, height:26 };
const filterTextInput = { flex:1, height:26, border:"1.5px solid #CBD5E1", borderRadius:5, fontSize:11, padding:"0 7px", outline:"none", fontFamily:FONT, background:"#fff", color:"#111827" };
const filterNumInput  = { flex:1, height:26, border:"1.5px solid #CBD5E1", borderRadius:5, fontSize:11, padding:"0 5px", outline:"none", fontFamily:FONT, background:"#fff", color:"#111827", textAlign:"right" };
const opBtnSt  = (active) => ({ flexShrink:0, width:22, height:26, border:`1.5px solid ${active?"#4F46E5":"#CBD5E1"}`, borderRadius:5, background:active?"#4F46E5":"#fff", fontSize:11, fontWeight:700, color:active?"#fff":"#4F46E5", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:0, fontFamily:FONT });
const clearBtnSt = { flexShrink:0, width:18, height:18, border:"none", borderRadius:"50%", background:"#E2E8F0", fontSize:9, color:"#64748B", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:0 };
const opDropSt = { position:"fixed", zIndex:99, background:"#fff", border:"1px solid #E2E8F0", borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", minWidth:190, overflow:"hidden", fontFamily:FONT };
const colsBtnSt = (active) => ({ display:"inline-flex", alignItems:"center", gap:6, height:30, padding:"0 12px", borderRadius:6, border:`1px solid ${active?"#C7D2FE":"#E2E8F0"}`, background:active?"#EEF2FF":"#fff", fontSize:12, fontWeight:500, color:active?"#4F46E5":"#374151", cursor:"pointer", fontFamily:FONT });
const hiddenBadgeSt = { display:"inline-flex", alignItems:"center", height:18, padding:"0 7px", borderRadius:10, background:"#4F46E5", color:"#fff", fontSize:11, fontWeight:600 };
const colPickerSt = { position:"absolute", top:"calc(100% + 4px)", right:0, zIndex:1200, background:"#fff", border:"1px solid #E2E8F0", borderRadius:10, boxShadow:"0 8px 28px rgba(0,0,0,0.12)", minWidth:200, maxHeight:420, overflowY:"auto", fontFamily:FONT };
const colPickerHdr = { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px 8px", borderBottom:"1px solid #F3F4F6" };
const colPickerItem = { display:"flex", alignItems:"center", gap:9, padding:"7px 14px", cursor:"pointer", fontSize:12, color:"#374151", userSelect:"none", borderBottom:"1px solid #F9FAFB" };

/* ── Pagination styles ── */
const pgBar    = { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 20px", borderTop:"1px solid #E2E8F0", background:"#FAFAFA", gap:12, flexWrap:"wrap" };
const pgInfo   = { fontSize:12, color:"#6B7280", fontFamily:FONT, whiteSpace:"nowrap" };
const pgRight  = { display:"flex", alignItems:"center", gap:8 };
const pgCtrl   = { display:"flex", alignItems:"center", gap:3 };
const pgSzLbl  = { fontSize:12, color:"#6B7280", fontFamily:FONT, whiteSpace:"nowrap" };
const pgSzSel  = { height:28, border:"1px solid #E2E8F0", borderRadius:6, fontSize:12, color:"#374151", padding:"0 6px", background:"#fff", cursor:"pointer", fontFamily:FONT };
const pgBtn    = (dis) => ({ minWidth:30, height:28, padding:"0 6px", border:"1px solid #E2E8F0", borderRadius:6, background:"#fff", color:dis?"#94A3B8":"#374151", fontSize:12, fontWeight:500, cursor:dis?"default":"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", fontFamily:FONT, opacity:dis?0.35:1 });
const pgBtnAct = { minWidth:30, height:28, padding:"0 6px", border:`1px solid ${TH_BG_BAND}`, borderRadius:6, background:TH_BG_BAND, color:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", display:"inline-flex", alignItems:"center", justifyContent:"center", fontFamily:FONT };
const pgDots   = { fontSize:12, color:"#6B7280", padding:"0 4px", userSelect:"none" };

/* ── Formatters ── */
const fmtHours = (v) => v == null ? "" : v.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });
const fmtMoney = (v) => {
  if (v == null) return "";
  const abs = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });
  return v < 0 ? `($${abs})` : `$${abs}`;
};
const fmtBr = (v) => v == null ? "" : `$${v.toFixed(2)}`;

/* ── SVG nav icons ── */
const IcoFirst = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 10L6 7l3-3M5 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoPrev  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M8.5 10.5L5 7l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoNext  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5.5 3.5L9 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoLast  = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 10l3-3-3-3M9 10V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;

/* ═══════════════════════════════════════════════════════════
   Component
═══════════════════════════════════════════════════════════ */
export default function ByLcat({ filters, onMeta }) {
  /* ── data state ── */
  const [loading,    setLoading]    = useState(true);
  const [rows,       setRows]       = useState([]);
  const [grandTotal, setGrandTotal] = useState({});
  const [actualThru, setActualThru] = useState(0);
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(25);

  /* ── filter state ── */
  const [textFilters, setTextFilters] = useState({});
  const [numFilters,  setNumFilters]  = useState({});
  const [sortKeys,    setSortKeys]    = useState([]);

  /* ── column picker ── */
  const [hiddenCols,    setHiddenCols]    = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  /* ── filter panel / op dropdown ── */
  const [openFilterKey,  setOpenFilterKey]  = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top:0, left:0 });
  const [openOpKey,      setOpenOpKey]      = useState(null);
  const [opDropdownPos,  setOpDropdownPos]  = useState({ top:0, left:0 });
  const filterBtnRefs    = useRef({});
  const opBtnRefs        = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);

  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current     = openOpKey;     }, [openOpKey]);

  /* Month/quarter never reach the API (postMonthlyTM strips them) — the full year is fetched
     once and narrowed to the selected month/quarter client-side below, so this key excludes them
     to keep the fetch from re-running when only month/quarter change. */
  const apiFilterKey = JSON.stringify({ ...filters, monthName: undefined, quarter: undefined });
  const globalPeriodHidden = useMemo(
    () => getGlobalPeriodHiddenKeys(filters, "Month", MONTH_KEYS),
    [filters?.monthName, filters?.quarter],
  );

  /* ── API fetch ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMonthlyTMByLcatAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        setRows(data?.rows ?? []);
        setGrandTotal(data?.grandTotal ?? {});
        setActualThru(data?.actualThroughMonth ?? 0);
        onMeta?.({ actualThroughMonth: data?.actualThroughMonth, fiscalYear: data?.fiscalYear });
        setPage(1);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Reposition filter panels on scroll ── */
  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) { const btn = filterBtnRefs.current[fk]; if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC)); }
      const ok = openOpKeyRef.current;
      if (ok) { const ob = opBtnRefs.current[ok]; if (ob) { const r = ob.getBoundingClientRect(); setOpDropdownPos({ top: r.bottom + 4, left: r.left }); } }
    };
    window.addEventListener("scroll", reposition, true);
    return () => window.removeEventListener("scroll", reposition, true);
  }, []);

  /* ── Close column picker on outside click ── */
  useEffect(() => {
    if (!colPickerOpen) return;
    const h = (e) => { if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setColPickerOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [colPickerOpen]);

  /* ── Close filter panel on outside click ── */
  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]"))
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  /* ── Close op dropdown on outside click ── */
  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]"))
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  /* ── Reset page when filters change ── */
  useEffect(() => { setPage(1); }, [textFilters, numFilters]);

  /* ── Filter helpers ── */
  const isNumFilterActive = useCallback((key) => {
    const f = numFilters[key]; return !!(f && f.value !== "" && f.value !== undefined);
  }, [numFilters]);
  const isFilterActive = useCallback((key) => {
    const col = FIXED_COLS.find((c) => c.key === key);
    return col && col.isText
      ? !!(textFilters[key] && textFilters[key].trim())
      : isNumFilterActive(key);
  }, [textFilters, isNumFilterActive]);

  const toggleFilter = useCallback((key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  }, [openFilterKey]);

  const toggleOpDropdown = useCallback((colKey) => {
    if (openOpKey === colKey) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[colKey];
    if (btn) { const r = btn.getBoundingClientRect(); setOpDropdownPos({ top: r.bottom + 4, left: r.left }); }
    setOpenOpKey(colKey);
  }, [openOpKey]);

  const selectOp        = (colKey, opKey) => { setNumFilters((p) => ({ ...p, [colKey]: { ...p[colKey], op: opKey } })); setOpenOpKey(null); };
  const updateNumFilter = (colKey, value) => setNumFilters((p) => ({ ...p, [colKey]: { op: "gt", ...p[colKey], value } }));
  const clearFilter = (key) => {
    const col = FIXED_COLS.find((c) => c.key === key);
    if (col && col.isText) setTextFilters((p) => { const n = { ...p }; delete n[key]; return n; });
    else setNumFilters((p) => { const n = { ...p }; delete n[key]; return n; });
  };

  /* ── Column picker helpers ── */
  const hideableCols = [
    ...FIXED_COLS.map((c) => ({ key: c.key, label: c.label })),
    ...MONTH_KEYS.map((k)  => ({ key: k,     label: MONTH_LABELS[k] })),
  ];
  const hiddenCount = hiddenCols.size;
  const toggleCol   = (key) => setHiddenCols((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const showAllCols = () => setHiddenCols(new Set());

  const getSortColumnLabel = useCallback((key) => {
    const fixed = FIXED_COLS.find((c) => c.key === key);
    return fixed ? fixed.label : key;
  }, []);

  const getSortValue = useCallback((row, key) => {
    const col = FIXED_COLS.find((c) => c.key === key);
    if (col?.align === "right") return Number(row[key]) || 0;
    return row[key] ?? "";
  }, []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  if (loading) return <Loading />;

  /* ── Derived month / sticky col lists ── */
  const months = MONTH_KEYS.map((key) => ({ key, label: MONTH_LABELS[key], isActual: MONTH_NUM[key] <= actualThru }));
  /* A selected global month/quarter filter overrides the table-level column
     picker — e.g. hiding Jan via the picker then selecting Jan globally
     should bring the Jan column back, not keep it hidden. */
  const visibleMonths = months.filter((m) =>
    globalPeriodHidden.has(m.key)
      ? false
      : globalPeriodHidden.size > 0 || !hiddenCols.has(m.key),
  );

  let fl = 0;
  const stickyCols = FIXED_COLS
    .filter((c) => !hiddenCols.has(c.key))
    .map((c) => { const out = { ...c, left: fl }; fl += c.w; return out; });
  const isLastSticky = (idx) => idx === stickyCols.length - 1;

  /* ── Filtered rows ── */
  const filteredRows = rows.filter((row) => {
    for (const fc of FIXED_COLS) {
      if (fc.isText) {
        const v = textFilters[fc.key];
        if (v && v.trim() && !String(row[fc.key] ?? "").toLowerCase().includes(v.trim().toLowerCase())) return false;
      } else if (isNumFilterActive(fc.key) && !applyNumFilter(row[fc.key], numFilters[fc.key])) {
        return false;
      }
    }
    for (const mk of MONTH_KEYS) {
      if (isNumFilterActive(`${mk}_hours`)   && !applyNumFilter(row.months?.[mk]?.hours,   numFilters[`${mk}_hours`]))   return false;
      if (isNumFilterActive(`${mk}_dollars`) && !applyNumFilter(row.months?.[mk]?.dollars, numFilters[`${mk}_dollars`])) return false;
    }
    return true;
  });

  const sortedRows = sortKeys.length === 0
    ? filteredRows
    : [...filteredRows].sort((a, b) => compareBySortKeys(a, b, sortKeys, getSortValue));

  /* ── Pagination ── */
  const totalRows  = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage   = Math.min(page, totalPages);
  const start      = (safePage - 1) * pageSize;
  const pageRows   = sortedRows.slice(start, start + pageSize);
  const pageNums   = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const s = new Set([1, totalPages, safePage, safePage-1, safePage+1, safePage-2, safePage+2]);
    return [...s].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  })();

  const stickyW    = stickyCols.reduce((s, c) => s + c.w, 0);
  const TABLE_W    = stickyW + visibleMonths.length * (COL_HOURS + COL_DOLLARS);
  const tableStyle = { borderCollapse:"collapse", borderSpacing:0, tableLayout:"fixed", fontFamily:FONT, fontSize:12, width:TABLE_W, minWidth:TABLE_W };

  const hasTableFilters = hasAnyTableFilters(
    hasActiveTextFilters(textFilters),
    hasActiveNumFilters(numFilters),
  );
  const displayGrandTotal = hasTableFilters
    ? aggregateMonthlyTmGrandTotal(filteredRows, MONTH_KEYS)
    : grandTotal;

  const getFilterLabel = (key) => {
    const fixed = FIXED_COLS.find((c) => c.key === key);
    if (fixed) return fixed.label;
    const us = key.indexOf("_");
    if (us > 0) {
      const mk = key.slice(0, us);
      const suffix = key.slice(us + 1);
      const month = MONTH_LABELS[mk] || mk;
      if (suffix === "hours") return `${month} Hours`;
      if (suffix === "dollars") return `${month} Dollars`;
    }
    return key;
  };

  /* ── Filter panel content ── */
  const renderFilterContent = (key) => {
    const col = FIXED_COLS.find((c) => c.key === key);
    const isText = col ? col.isText : false;
    if (isText) {
      const val = textFilters[key] || "";
      return (
        <TextFilterContent
          label={getFilterLabel(key)}
          value={val}
          onChange={(v) => setTextFilters((p) => ({ ...p, [key]: v }))}
          onClear={() => clearFilter(key)}
        />
      );
    }
    const f = numFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getFilterLabel(key)}
        value={f.value ?? ""}
        op={f.op || "gt"}
        onValueChange={(v) => updateNumFilter(key, v)}
        onClear={() => clearFilter(key)}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => { opBtnRefs.current[key] = el; }}
      />
    );
  };

  /* ════════════════════════════════════════════════════════ */
  return (
    <div style={CARD}>
      {/* ── Card header ── */}
      <div style={HEAD}>
        <div>
          <h2 style={TITLE}>By LCAT</h2>
          {sortKeys.length > 0 && (
            <div style={CARD_META}>
              <span style={{ color: SC_ACCENT, fontWeight: 500 }}>
                Sorted by {sortKeys.map((s) => getSortColumnLabel(s.key)).join(", ")}
              </span>
            </div>
          )}
        </div>
        <div ref={colPickerRef} style={{ position:"relative", flexShrink:0 }}>
          <button style={colsBtnSt(colPickerOpen || hiddenCount > 0)} onClick={() => setColPickerOpen((p) => !p)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="18"/>
            </svg>
            Columns
            {hiddenCount > 0 && <span style={hiddenBadgeSt}>{hiddenCount} Hidden</span>}
          </button>
          {colPickerOpen && (
            <div style={colPickerSt}>
              <div style={colPickerHdr}>
                <span style={{ fontSize:11, fontWeight:700, color:"#6B7280", letterSpacing:"0.05em", textTransform:"uppercase" }}>Show / Hide</span>
                {hiddenCount > 0 && (
                  <button style={{ fontSize:11, fontWeight:600, color:"#4F46E5", background:"none", border:"none", cursor:"pointer", padding:0, fontFamily:FONT }}
                    onClick={showAllCols}>Show All</button>
                )}
              </div>
              {hideableCols.map((col) => (
                <label key={col.key} style={colPickerItem}>
                  <input type="checkbox" style={{ width:14, height:14, accentColor:"#4F46E5", cursor:"pointer", flexShrink:0 }}
                    checked={!hiddenCols.has(col.key)} onChange={() => toggleCol(col.key)} />
                  {col.label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={CARD_BODY}>
      {/* ── Table ── */}
      <div style={{ overflowX:"auto" }}>
        <table style={tableStyle}>
          <colgroup>
            {stickyCols.map((c)  => <col key={c.key} style={{ width:c.w }} />)}
            {visibleMonths.map((m) => (
              <React.Fragment key={m.key}>
                <col style={{ width:COL_HOURS }} />
                <col style={{ width:COL_DOLLARS }} />
              </React.Fragment>
            ))}
          </colgroup>
          <thead>
            {/* Row 1: fixed cols show the selected year + month group headers */}
            <tr style={{ height:GH }}>
              {stickyCols.length > 0 && (
                <th colSpan={stickyCols.length} style={thFixedGroup(stickyW)}>
                  {filters?.year ? `Year - ${filters.year}` : ""}
                </th>
              )}
              {visibleMonths.map((m) => (
                <th key={m.key} colSpan={2} style={thScrollGroup(m.isActual)}>{m.label}</th>
              ))}
            </tr>
            {/* Row 2: individual fixed col headers + Hours / Dollars */}
            <tr style={{ height:CH }}>
              {stickyCols.map((c, ci) => (
                <th key={c.key} style={{ ...thFixedCol(c, isLastSticky(ci)), cursor: "pointer" }} onClick={onThClick(c.key)}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent: c.align === "right" ? "flex-end" : "space-between", gap:4 }}>
                    <WaterfallThLabel
                      label={c.label}
                      colKey={c.key}
                      sortKeys={sortKeys}
                      getLabel={getSortColumnLabel}
                      highlight={openFilterKey === c.key}
                      align={c.align}
                    />
                    <button ref={(el) => { filterBtnRefs.current[c.key] = el; }} data-filter-btn
                      style={filterIconBtn(isFilterActive(c.key) || openFilterKey === c.key)}
                      onClick={() => toggleFilter(c.key)} title="Filter">
                      <FilterFunnelIcon />
                    </button>
                  </div>
                </th>
              ))}
              {visibleMonths.map((m) => (
                <React.Fragment key={m.key}>
                  <th style={thScrollCol(m.isActual)}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:3 }}>
                      <span>Hours</span>
                      <button ref={(el) => { filterBtnRefs.current[`${m.key}_hours`] = el; }} data-filter-btn
                        style={filterIconBtn(isFilterActive(`${m.key}_hours`) || openFilterKey === `${m.key}_hours`, m.isActual)}
                        onClick={() => toggleFilter(`${m.key}_hours`)} title="Filter hours">
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                  <th style={thScrollCol(m.isActual)}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:3 }}>
                      <span>Dollars</span>
                      <button ref={(el) => { filterBtnRefs.current[`${m.key}_dollars`] = el; }} data-filter-btn
                        style={filterIconBtn(isFilterActive(`${m.key}_dollars`) || openFilterKey === `${m.key}_dollars`, m.isActual)}
                        onClick={() => toggleFilter(`${m.key}_dollars`)} title="Filter dollars">
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Total row */}
            <tr style={{ height:RH }}>
              {stickyCols.map((c, ci) => (
                <td key={c.key} style={tdTotal(c, isLastSticky(ci))}>{ci === 0 ? "Total" : ""}</td>
              ))}
              {visibleMonths.map((m) => {
                const d = displayGrandTotal[m.key];
                return (
                  <React.Fragment key={m.key}>
                    <td style={tdTotalScroll()}>{fmtHours(d?.hours)}</td>
                    <td style={tdTotalScroll()}>{fmtMoney(d?.dollars)}</td>
                  </React.Fragment>
                );
              })}
            </tr>
            {/* Data rows */}
            {pageRows.map((row, i) => {
              const bg = i % 2 === 0 ? EVEN_BG : ODD_BG;
              return (
                <tr key={i} style={{ height:RH }}>
                  {stickyCols.map((c, ci) => (
                    <td key={c.key} style={tdFixed(bg, c, 400, isLastSticky(ci))} title={String(row[c.key] ?? "")}>
                      {c.key === "billRate" ? fmtBr(row[c.key]) : row[c.key] ?? ""}
                    </td>
                  ))}
                  {visibleMonths.map((m) => {
                    const d = row.months?.[m.key];
                    return (
                      <React.Fragment key={m.key}>
                        <td style={tdBase(bg)}>{fmtHours(d?.hours)}</td>
                        <td style={tdBase(bg)}>{fmtMoney(d?.dollars)}</td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={stickyCols.length + visibleMonths.length * 2}
                  style={{ padding:"24px 20px", textAlign:"center", color:"#9CA3AF", fontSize:13, fontFamily:FONT }}>
                  {rows.length === 0 ? "No data available." : "No rows match the current filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {rows.length > 0 && (
        <div style={pgBar}>
          <span style={pgInfo}>
            {totalRows === 0 ? "No rows match the current filters" : `${start + 1}–${Math.min(start + pageSize, totalRows)} of ${totalRows} rows`}
          </span>
          <div style={pgRight}>
            <span style={pgSzLbl}>Rows Per Page</span>
            <select style={pgSzSel} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10,25,50,100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <div style={pgCtrl}>
              <button style={pgBtn(safePage === 1)} disabled={safePage === 1} onClick={() => setPage(1)} title="First"><IcoFirst /></button>
              <button style={pgBtn(safePage === 1)} disabled={safePage === 1} onClick={() => setPage((p) => p - 1)} title="Previous"><IcoPrev /></button>
              {pageNums.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && pageNums[i-1] < p - 1 && <span style={pgDots}>…</span>}
                  <button style={p === safePage ? pgBtnAct : pgBtn(false)} onClick={() => setPage(p)}>{p}</button>
                </React.Fragment>
              ))}
              <button style={pgBtn(safePage === totalPages)} disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)} title="Next"><IcoNext /></button>
              <button style={pgBtn(safePage === totalPages)} disabled={safePage === totalPages} onClick={() => setPage(totalPages)} title="Last"><IcoLast /></button>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* ── Filter panel portal ── */}
      {openFilterKey && createPortal(
        <div data-filter-panel style={{ ...fpBase, top: filterPanelPos.top, left: filterPanelPos.left }}>
          {renderFilterContent(openFilterKey)}
        </div>,
        document.body
      )}

      {/* ── Operator dropdown portal ── */}
      {openOpKey && createPortal(
        <OpDropdownContent
          activeOp={(numFilters[openOpKey]?.op) || "gt"}
          onSelect={(op) => selectOp(openOpKey, op)}
          style={{ top: opDropdownPos.top, left: opDropdownPos.left }}
        />,
        document.body
      )}
    </div>
  );
}
